import type { FormEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Github, Mail, Apple, Loader2, Lock, Globe } from 'lucide-react';
import { LanguageSwitcher } from '@/components/language-switcher';
import { useTranslation } from 'react-i18next';
import { authService } from '@/services/auth.service';
import { useProvider } from '@/hooks/useTenant';
import { useBrandLogo } from '@/hooks/useBrandLogo';
import { usePasswordLoginAvailability } from '@/hooks/auth/usePasswordLoginAvailability';
import { useAuth } from '@/contexts/AuthContext';
import { useSocialAuth } from '@/plugins/social-auth/useSocialAuth';
import { useAvailableSocialProviders } from '@/plugins/social-auth/useAvailableSocialProviders';
import type { OAuthProvider } from '@/types/auth';
import { GoogleIcon } from '@/components/icons/GoogleIcon';
import { MicrosoftIcon } from '@/components/icons/MicrosoftIcon';
import { getCurrentDomain } from '@/utils/domain';
import { normalizeEmail } from '@/lib/email';

const SOCIAL_PROVIDER_ORDER: OAuthProvider[] = ['google', 'apple', 'github', 'azure'];
const PROVIDER_LABEL_KEYS: Record<OAuthProvider, string> = {
  google: 'auth.continueWithGoogle',
  apple: 'auth.continueWithApple',
  github: 'auth.continueWithGithub',
  azure: 'auth.continueWithMicrosoft',
};
const PROVIDER_LABEL_FALLBACK: Record<OAuthProvider, string> = {
  google: 'Continue with Google',
  apple: 'Continue with Apple',
  github: 'Continue with GitHub',
  azure: 'Continue with Microsoft',
};

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

function resolveAuthErrorMessage(error: unknown, fallback: string): string {
  if (!error) {
    return fallback;
  }

  if (isNonEmptyString(error)) {
    return error;
  }

  if (typeof error === 'object') {
    const record = error as Record<string, unknown>;
    if (isNonEmptyString(record.message)) {
      return record.message;
    }

    const response = record.response as Record<string, unknown> | undefined;
    const responseData = response?.data as Record<string, unknown> | undefined;
    if (responseData && isNonEmptyString(responseData.message)) {
      return responseData.message;
    }
    if (responseData && isNonEmptyString(responseData.error)) {
      return responseData.error;
    }
  }

  return fallback;
}

function resolveAuthErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const record = error as Record<string, unknown>;
  const response = record.response as Record<string, unknown> | undefined;
  const responseStatus = response?.status;
  if (typeof responseStatus === 'number') {
    return responseStatus;
  }

  if (typeof record.status === 'number') {
    return record.status;
  }

  return undefined;
}

export default function ProviderLogin() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const provider = useProvider();
  const { login: establishProviderSession } = useAuth();
  const brandLogo = useBrandLogo();
  const brandName = provider?.name ?? provider?.slug ?? t('navigation.providerAccount');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [socialError, setSocialError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [usePassword, setUsePassword] = useState(false);
  const domainInfo = useMemo(() => getCurrentDomain(), []);
  const resolvedTenantSlug = provider?.slug ?? domainInfo.subdomain ?? undefined;
  const { enabled: passwordLoginEnabled } = usePasswordLoginAvailability('provider', {
    tenantSlug: resolvedTenantSlug,
  });
  const {
    providers: enabledSocialProviders,
    providerStatus,
    isLoading: isSocialConfigLoading,
  } = useAvailableSocialProviders({
    scope: 'provider',
    tenantSlug: resolvedTenantSlug,
  });

  const { startLogin: startSocialLogin, isLoading: isSocialLoading, activeProvider, error: socialPluginError } =
    useSocialAuth({
      scope: 'provider',
      tenantSlug: resolvedTenantSlug,
      loginHint: normalizeEmail(email) || undefined,
      prompt: 'select_account',
      onSuccess: async (payload) => {
        setSocialError(null);
        await establishProviderSession(payload.user, payload.provider, payload.token);
        navigate('/provider/dashboard', { replace: true });
      },
      onError: (message) => {
        setSocialError(message);
      },
    });

  const visibleSocialProviders = useMemo<OAuthProvider[]>(() => {
    if (enabledSocialProviders.length === 0) {
      return [];
    }

    const enabledSet = new Set(enabledSocialProviders);
    const ordered = SOCIAL_PROVIDER_ORDER.filter((providerId) => enabledSet.has(providerId));
    const extras = enabledSocialProviders.filter(
      (providerId) => !SOCIAL_PROVIDER_ORDER.includes(providerId),
    );

    return [...ordered, ...extras];
  }, [enabledSocialProviders]);

  const providerStatusMap = useMemo(
    () => new Map(providerStatus.map((item) => [item.provider, item])),
    [providerStatus],
  );

  const resolveProviderLabel = (providerId: OAuthProvider) => {
    const statusLabel = providerStatusMap.get(providerId)?.label;
    if (statusLabel) {
      return t('auth.continueWithProvider', 'Continue with {{provider}}', { provider: statusLabel });
    }
    return t(PROVIDER_LABEL_KEYS[providerId], PROVIDER_LABEL_FALLBACK[providerId]);
  };

  const resolveProviderIcon = (providerId: OAuthProvider) => {
    const iconName = providerStatusMap.get(providerId)?.icon?.toLowerCase();
    if (iconName === 'microsoft' || providerId === 'azure') {
      return MicrosoftIcon;
    }
    if (providerId === 'google') {
      return GoogleIcon;
    }
    if (providerId === 'apple') {
      return Apple;
    }
    if (providerId === 'github') {
      return Github;
    }
    return Globe;
  };

  const shouldShowSocialLogins = !isSocialConfigLoading && visibleSocialProviders.length > 0;
  const socialGridClass =
    visibleSocialProviders.length === 1
      ? 'grid-cols-1'
      : visibleSocialProviders.length === 2
        ? 'grid-cols-2'
        : 'grid-cols-3';

  useEffect(() => {
    if (socialPluginError && socialPluginError !== socialError) {
      setSocialError(socialPluginError);
    }
  }, [socialPluginError, socialError]);

  useEffect(() => {
    if (!passwordLoginEnabled) {
      setUsePassword(false);
    }
  }, [passwordLoginEnabled]);

  const requestMagicLink = async () => {
    setError('');
    setPasswordError('');
    setSocialError(null);
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const normalizedEmail = normalizeEmail(email);
    if (!emailRegex.test(normalizedEmail)) {
      setError(t('auth.invalidEmail'));
      return;
    }

    setIsLoading(true);
    
    try {
      await authService.sendProviderOTP({ email: normalizedEmail });
      // Navigate to OTP verification page (provider portal)
      navigate('/provider/login/verify', { state: { email: normalizedEmail } });
    } catch (err) {
      setError(resolveAuthErrorMessage(err, t('auth.errorSendingCode')));
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await requestMagicLink();
  };

  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setPasswordError('');
    setSocialError(null);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const normalizedEmail = normalizeEmail(email);
    if (!emailRegex.test(normalizedEmail)) {
      setPasswordError(t('auth.invalidEmail'));
      return;
    }
    if (password.trim().length < 8) {
      setPasswordError(
        t('auth.passwordTooShort', 'Password must be at least 8 characters long.'),
      );
      return;
    }

    setIsPasswordLoading(true);

    try {
      const response = await authService.loginProviderWithPassword({
        email: normalizedEmail,
        password,
      });
      const payload = response.data;
      if (!payload?.token) {
        setPasswordError(t('auth.invalidCredentials', 'Invalid credentials provided.'));
        setIsPasswordLoading(false);
        return;
      }
      await establishProviderSession(payload.user, payload.provider, payload.token);
      navigate('/provider/dashboard', { replace: true });
    } catch (err) {
      const status = resolveAuthErrorStatus(err);
      if (status === 423) {
        setPasswordError(
          t('auth.passwordLoginDisabled', 'Password login is disabled for this provider.'),
        );
      } else {
        setPasswordError(
          resolveAuthErrorMessage(
            err,
            t('auth.invalidCredentials', 'Invalid credentials provided.'),
          ),
        );
      }
      setIsPasswordLoading(false);
      return;
    }

    setIsPasswordLoading(false);
  };

  const handleSocialButtonClick = (providerId: OAuthProvider) => {
    setSocialError(null);
    if (isLoading || isSocialLoading) {
      return;
    }

    void startSocialLogin(providerId);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 relative">
      {/* Language Switcher in top right corner */}
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>
      
      <div className="w-full max-w-md space-y-6">
        <div className="flex flex-col items-center space-y-1">
          {brandLogo ? (
            <img
              src={brandLogo}
              alt={`${brandName} logo`}
              className="h-10 w-auto max-w-[200px] object-contain"
            />
          ) : (
            <span className="text-2xl font-semibold text-foreground">{brandName}</span>
          )}
        </div>

        <Card className="w-full">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl">{t('auth.providerSignIn')}</CardTitle>
            <CardDescription>
              {t('auth.accessDashboard')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form
              onSubmit={passwordLoginEnabled && usePassword ? handlePasswordSubmit : handleEmailSubmit}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="provider-email">{t('auth.email')}</Label>
                <Input
                  id="provider-email"
                  type="email"
                  placeholder={t('auth.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading || isPasswordLoading}
                  required
                />
              </div>

              {passwordLoginEnabled && usePassword ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="provider-password">{t('auth.password', 'Password')}</Label>
                    <Input
                      id="provider-password"
                      type="password"
                      placeholder={t('auth.passwordPlaceholder', 'Your password')}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isPasswordLoading || isLoading}
                      required
                    />
                  </div>
                </>
              ) : null}

              {passwordLoginEnabled && usePassword ? (
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto p-0 text-xs"
                    onClick={() => navigate('/provider/forgot-password')}
                    disabled={isPasswordLoading || isLoading}
                  >
                    {t('auth.forgotPassword.cta')}
                  </Button>
                </div>
              ) : null}

              {passwordLoginEnabled && usePassword && passwordError ? (
                <p className="text-sm text-destructive">{passwordError}</p>
              ) : null}
              {!usePassword && error ? (
                <p className="text-sm text-destructive">{error}</p>
              ) : null}

              <Button
                type="submit"
                className="w-full"
                disabled={isLoading || isPasswordLoading}
              >
                {passwordLoginEnabled && usePassword ? (
                  isPasswordLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('auth.oauthSigningIn', 'Signing you in')}
                    </>
                  ) : (
                    <>
                      <Lock className="mr-2 h-4 w-4" />
                      {t('auth.passwordSignIn', 'Sign in with password')}
                    </>
                  )
                ) : isLoading ? (
                  <>
                    <Mail className="mr-2 h-4 w-4 animate-pulse" />
                    {t('auth.sendingCode')}
                  </>
                ) : (
                  <>
                    <Mail className="mr-2 h-4 w-4" />
                    {t('auth.continueWithEmail')}
                  </>
                )}
              </Button>
            </form>

            {passwordLoginEnabled ? (
              <div className="space-y-3 pt-1">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <Separator />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      {t('common.or', 'OR')}
                    </span>
                  </div>
                </div>
                {error ? <p className="text-sm text-destructive">{error}</p> : null}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setUsePassword((prev) => !prev);
                    setError('');
                    setPasswordError('');
                  }}
                  disabled={isLoading || isPasswordLoading}
                >
                  {usePassword
                    ? t('auth.useSignInCode', 'Use sign-in code')
                    : t('auth.usePassword', 'Use password')}
                </Button>
              </div>
            ) : null}

            {socialError ? (
              <p className="text-sm text-destructive">{socialError}</p>
            ) : null}

            {shouldShowSocialLogins ? (
              <>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <Separator />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      {t('auth.orContinueWith')}
                    </span>
                  </div>
                </div>

                {/* OAuth Buttons */}
                <div className={`grid gap-3 ${socialGridClass}`}>
                  {visibleSocialProviders.map((providerId) => {
                    const ProviderIcon = resolveProviderIcon(providerId);
                    return (
                    <Button
                      key={providerId}
                      variant="outline"
                      onClick={() => handleSocialButtonClick(providerId)}
                      disabled={isLoading || isSocialLoading}
                      aria-label={resolveProviderLabel(providerId)}
                    >
                      {isSocialLoading && activeProvider === providerId ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <ProviderIcon className="h-5 w-5" aria-hidden="true" />
                      )}
                    </Button>
                    );
                  })}
                </div>
              </>
            ) : null}
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <div className="flex flex-wrap justify-center gap-4 text-xs text-muted-foreground">
              <a href="#" className="hover:text-primary">{t('common.privacyPolicy')}</a>
              <a href="#" className="hover:text-primary">{t('common.termsOfService')}</a>
              <a href="#" className="hover:text-primary">{t('common.helpSupport')}</a>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
